import { calendarDisplayDate } from '../../../../utils/date-time';
import React, { useCallback, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useFilterApp } from '../../../../contexts/FilterApp';
import { AtendentesService } from '../../../atendentes/Service';
import AvatarUser from '../../../../components/AvatarUser';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { useEstabsContext } from '../../../../contexts/EstabsContext';
import { useParamsContext } from '../../../../contexts/ParamsContext';
import {
    AGENDA_SETTINGS_SECTIONS,
    DEFAULT_AGENDA_SETTINGS_SECTION,
} from '../settings/sections';
import '../list/Calendar.css';
import { Check, ChevronLeft, ChevronRight, Plus, ChevronDown, ChevronUp } from 'lucide-react';

const service = new AtendentesService();

type AttendantOption = {
    uid: string;
    displayName: string;
    photoURL?: string;
    type?: string;
};

const getFirstName = (name?: string) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return '';
    return trimmed.split(/\s+/)[0] || trimmed;
};

const formatDateLabel = (date: Date) => {
    const label = moment(date).locale('pt-br').format('dddd, D [de] MMMM');
    return label.charAt(0).toUpperCase() + label.slice(1);
};

const hasValidSlots = (slots?: { start: number; end: number }[]) =>
    Array.isArray(slots) && slots.some((slot) => Number(slot?.start) >= 0 && Number(slot?.end) > Number(slot?.start));

const getDateKey = (date: Date | moment.Moment) => moment(date).format('YYYY-MM-DD');

const getRelativeDayLabel = (date: Date, timezone?: string) => {
    const diff = moment(date).startOf('day').diff(moment(calendarDisplayDate(new Date(), timezone)).startOf('day'), 'days');
    if (diff === 0) return 'Hoje';
    if (diff === 1) return 'Amanhã';
    if (diff === -1) return 'Ontem';
    return '';
};

// `sidebar`: painel lateral do desktop; `compact`: topo da agenda em telas ate 1024px.
type FilterAgendaVariant = 'sidebar' | 'compact';

export const FilterAgenda = ({ variant = 'sidebar' }: { variant?: FilterAgendaVariant }) => {
    const { attendantId, setAttendantId, date, setDate } = useFilterApp();
    const { action, id, setParams } = useParamsContext();
    const { user } = useAuthContext();
    const { estabSession } = useEstabsContext();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [attendants, setAttendants] = useState<AttendantOption[]>([]);
    const [agendaConfig, setAgendaConfig] = useState<{
        disabled?: boolean;
        daysOpen?: number;
        maxScheduleDate?: string | Date | null;
    }>({});
    const [visibleMonth, setVisibleMonth] = useState<Date>(date);
    const [availableDateKeys, setAvailableDateKeys] = useState<Set<string>>(new Set());
    const [monthExpanded, setMonthExpanded] = useState(false);
    const isCompact = variant === 'compact';
    const isSettingsView = action === 'settings';
    const canAddAttendant = !!(user?.root || user?.admin);
    const visibleSettingsSections = AGENDA_SETTINGS_SECTIONS.filter(
        (section) => !section.adminOnly || user?.root || user?.admin
    );
    const activeSettingsSection =
        visibleSettingsSections.find((section) => section.key === id)?.key || DEFAULT_AGENDA_SETTINGS_SECTION;

    const isDateInsideAgendaWindow = useCallback((currentDate: Date | moment.Moment) => {
        const current = moment(currentDate).startOf('day');
        const today = moment(calendarDisplayDate(new Date(), estabSession?.timezone)).startOf('day');
        const daysOpen = Number(agendaConfig?.daysOpen || 0);
        const maxByDaysOpen = daysOpen > 0 ? today.clone().add(daysOpen, 'days') : null;
        const maxByConfig = agendaConfig?.maxScheduleDate ? moment(agendaConfig.maxScheduleDate).startOf('day') : null;

        if (current.isBefore(today, 'day')) {
            return false;
        }

        if (maxByDaysOpen && current.isAfter(maxByDaysOpen, 'day')) {
            return false;
        }

        if (maxByConfig?.isValid() && current.isAfter(maxByConfig, 'day')) {
            return false;
        }

        return true;
    }, [agendaConfig?.daysOpen, agendaConfig?.maxScheduleDate, estabSession?.timezone]);

    useEffect(() => {
        let isMounted = true;

        const loadAtendentes = async () => {
            try {
                setLoading(true);
                const response = await service.getAll();
                const data: AttendantOption[] = response?.data?.data || [];
                const restrictAttendantsSelect = estabSession?.allowAttendantsSeeOthersInSelect === false;
                const isPrivileged = !!(user?.root || user?.admin);
                const filtered =
                    restrictAttendantsSelect && !isPrivileged
                        ? data.filter((item: any) => item.uid === user?.uid)
                        : data;

                if (!isMounted) return;
                // Prioritize the signed-in attendant without changing the remaining order.
                const currentIndex = user?.uid
                    ? filtered.findIndex((attendant) => attendant.uid === user.uid)
                    : -1;
                setAttendants(currentIndex > 0
                    ? [filtered[currentIndex], ...filtered.slice(0, currentIndex), ...filtered.slice(currentIndex + 1)]
                    : filtered);

                // When the select is restricted, ensure the filter stays on the current user.
                if (restrictAttendantsSelect && !isPrivileged && user?.uid) {
                    setAttendantId(user.uid);
                }
            } catch (error) {
                console.error('Erro ao carregar atendentes:', error);
                if (isMounted) setAttendants([]);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadAtendentes();

        return () => {
            isMounted = false;
        };
    }, [setAttendantId, user?.admin, user?.root, user?.uid, estabSession?.allowAttendantsSeeOthersInSelect]);

    useEffect(() => {
        if (loading || !attendants.length) {
            return;
        }

        const hasSelectedAttendant = attendants.some((attendant) => attendant.uid === attendantId);

        if (!hasSelectedAttendant) {
            setAttendantId(attendants[0].uid);
        }
    }, [attendantId, attendants, loading, setAttendantId]);

    useEffect(() => {
        let isMounted = true;

        const loadAgendaConfig = async () => {
            if (!attendantId) {
                setAgendaConfig({});
                return;
            }

            try {
                const { data } = await service.getAgenda(attendantId);
                if (isMounted) {
                    setAgendaConfig(data || {});
                }
            } catch (error) {
                console.error('Erro ao carregar agenda do atendente:', error);
                if (isMounted) {
                    setAgendaConfig({});
                }
            }
        };

        loadAgendaConfig();

        return () => {
            isMounted = false;
        };
    }, [attendantId]);

    useEffect(() => {
        let isMounted = true;

        const loadAvailableDates = async () => {
            if (!attendantId) {
                setAvailableDateKeys(new Set());
                return;
            }

            const month = moment(visibleMonth);
            const firstDay = month.clone().startOf('month').startOf('week');
            const lastDay = month.clone().endOf('month').endOf('week');
            const dates: moment.Moment[] = [];

            for (let current = firstDay.clone(); current.isSameOrBefore(lastDay, 'day'); current.add(1, 'day')) {
                dates.push(current.clone());
            }

            const availableEntries = await Promise.all(
                dates.map(async (current) => {
                    if (!isDateInsideAgendaWindow(current)) {
                        return null;
                    }

                    try {
                        const { data } = await service.getAgendaDate(
                            attendantId,
                            current.format('YYYY-MM-DD'),
                            estabSession?._id,
                        );
                        return hasValidSlots(data?.horario) ? current.format('YYYY-MM-DD') : null;
                    } catch (error) {
                        console.error('Erro ao carregar disponibilidade do dia:', error);
                        return null;
                    }
                })
            );

            if (isMounted) {
                setAvailableDateKeys(new Set(availableEntries.filter(Boolean) as string[]));
            }
        };

        loadAvailableDates();

        return () => {
            isMounted = false;
        };
    }, [
        agendaConfig?.daysOpen,
        agendaConfig?.maxScheduleDate,
        attendantId,
        estabSession?._id,
        isDateInsideAgendaWindow,
        visibleMonth,
    ]);

    const changeDay = (amount: number) => {
        const nextDate = moment(date).add(amount, 'day').toDate();
        setDate(nextDate);
        setVisibleMonth(nextDate);
    };

    const selectDate = (nextDate: Date) => {
        setDate(nextDate);
        setVisibleMonth(nextDate);
    };

    const isToday = moment(date).isSame(moment(calendarDisplayDate(new Date(), estabSession?.timezone)), 'day');
    const relativeDayLabel = getRelativeDayLabel(date, estabSession?.timezone);
    const weekStart = moment(date).startOf('week');
    const weekDays = Array.from({ length: 7 }, (_, index) => weekStart.clone().add(index, 'day'));

    const getDateAvailabilityClass = (calendarDate: Date) => {
        if (!isDateInsideAgendaWindow(calendarDate)) {
            return '';
        }

        return availableDateKeys.has(getDateKey(calendarDate)) ? 'agenda-datepicker-day-available' : '';
    };

    const attendantsBlock = (
        <section className="agenda-filter-block" aria-label="Profissionais">
            {!isCompact && <h2 className="agenda-filter-title">Profissionais</h2>}
            {loading ? (
                <div className={isCompact ? 'agenda-attendant-strip' : 'agenda-attendant-list'} aria-busy="true">
                    {[0, 1, 2].map((item) => <span key={item} className="agenda-skeleton" />)}
                </div>
            ) : isCompact ? (
                <div className="agenda-attendant-strip">
                    {attendants.map((attendant) => {
                        const isSelected = attendant.uid === attendantId;
                        return (
                            <button
                                key={attendant.uid}
                                type="button"
                                className={`agenda-attendant-chip ${isSelected ? 'is-selected' : ''}`}
                                onClick={() => setAttendantId(attendant.uid)}
                                title={attendant.displayName}
                                aria-pressed={isSelected}
                            >
                                <AvatarUser size="w-11" name={attendant.displayName} photoURL={attendant.photoURL} />
                                <span className="truncate">{getFirstName(attendant.displayName)}</span>
                            </button>
                        );
                    })}
                    {canAddAttendant && (
                        <button
                            type="button"
                            className="agenda-attendant-chip agenda-attendant-add"
                            onClick={() => navigate('/atendentes?action=add')}
                            aria-label="Adicionar profissional"
                        >
                            <span className="agenda-attendant-add-icon"><Plus aria-hidden="true" /></span>
                            <span className="truncate">Adicionar</span>
                        </button>
                    )}
                </div>
            ) : (
                <ul className="agenda-attendant-list">
                    {attendants.map((attendant) => {
                        const isSelected = attendant.uid === attendantId;
                        return (
                            <li key={attendant.uid}>
                                <button
                                    type="button"
                                    className={`agenda-attendant-row ${isSelected ? 'is-selected' : ''}`}
                                    onClick={() => setAttendantId(attendant.uid)}
                                    aria-pressed={isSelected}
                                >
                                    <AvatarUser size="w-7" name={attendant.displayName} photoURL={attendant.photoURL} />
                                    <span className="min-w-0 flex-1 truncate">
                                        {attendant.displayName}
                                        {attendant.uid === user?.uid && <span className="agenda-attendant-me"> (você)</span>}
                                    </span>
                                    {isSelected && <Check className="agenda-attendant-check" aria-hidden="true" />}
                                </button>
                            </li>
                        );
                    })}
                    {canAddAttendant && (
                        <li>
                            <button
                                type="button"
                                className="agenda-attendant-row agenda-attendant-add"
                                onClick={() => navigate('/atendentes?action=add')}
                            >
                                <span className="agenda-attendant-add-icon"><Plus aria-hidden="true" /></span>
                                <span className="flex-1">Adicionar profissional</span>
                            </button>
                        </li>
                    )}
                </ul>
            )}
        </section>
    );

    const datePicker = (
        <DatePicker
            selected={date}
            onChange={(nextDate) => {
                if (!nextDate) return;
                selectDate(nextDate);
                if (isCompact) setMonthExpanded(false);
            }}
            onMonthChange={(nextDate) => setVisibleMonth(nextDate)}
            inline
            locale="pt-BR"
            calendarClassName="agenda-datepicker"
            dayClassName={getDateAvailabilityClass}
            formatWeekDay={(dayName) => dayName.charAt(0).toUpperCase()}
            renderCustomHeader={({ date: monthDate, decreaseMonth, increaseMonth }) => (
                <div className="agenda-datepicker-header">
                    <span>{moment(monthDate).locale('pt-br').format('MMMM [de] YYYY')}</span>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => {
                                decreaseMonth();
                                setVisibleMonth(moment(monthDate).subtract(1, 'month').toDate());
                            }}
                            aria-label="Mês anterior"
                        >
                            <ChevronLeft aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                increaseMonth();
                                setVisibleMonth(moment(monthDate).add(1, 'month').toDate());
                            }}
                            aria-label="Próximo mês"
                        >
                            <ChevronRight aria-hidden="true" />
                        </button>
                    </div>
                </div>
            )}
        />
    );

    const legend = (
        <p className="agenda-calendar-legend">
            <span className="agenda-calendar-legend-dot" aria-hidden="true" />
            Dias com horários livres
        </p>
    );

    const dateBlock = isCompact ? (
        <section className="agenda-filter-block" aria-label="Data">
            <div className="agenda-date-head">
                <button
                    type="button"
                    className="agenda-month-toggle"
                    onClick={() => setMonthExpanded(!monthExpanded)}
                    aria-expanded={monthExpanded}
                >
                    {moment(date).locale('pt-br').format('MMMM [de] YYYY')}
                    {monthExpanded ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
                </button>
                <div className="flex items-center gap-1">
                    {!isToday && (
                        <button type="button" className="agenda-today-button" onClick={() => selectDate(calendarDisplayDate(new Date(), estabSession?.timezone))}>
                            Hoje
                        </button>
                    )}
                    {!monthExpanded && (
                        <>
                            <button type="button" className="agenda-day-nav-button" onClick={() => changeDay(-7)} aria-label="Semana anterior">
                                <ChevronLeft aria-hidden="true" />
                            </button>
                            <button type="button" className="agenda-day-nav-button" onClick={() => changeDay(7)} aria-label="Próxima semana">
                                <ChevronRight aria-hidden="true" />
                            </button>
                        </>
                    )}
                </div>
            </div>

            {monthExpanded ? (
                <>
                    {datePicker}
                    {legend}
                </>
            ) : (
                // Faixa da semana (estilo Calendario do iPhone): letra do dia, numero e ponto de disponibilidade.
                <div className="agenda-week-strip" role="group" aria-label="Dias da semana">
                    {weekDays.map((day) => {
                        const isSelected = day.isSame(date, 'day');
                        const isDayToday = day.isSame(moment(calendarDisplayDate(new Date(), estabSession?.timezone)), 'day');
                        const isAvailable = isDateInsideAgendaWindow(day) && availableDateKeys.has(getDateKey(day));
                        return (
                            <button
                                key={getDateKey(day)}
                                type="button"
                                className={`agenda-week-day ${isSelected ? 'is-selected' : ''} ${isDayToday ? 'is-today' : ''}`}
                                onClick={() => selectDate(day.toDate())}
                                aria-pressed={isSelected}
                                aria-label={`${day.locale('pt-br').format('dddd, D [de] MMMM')}${isAvailable ? ', com horários livres' : ''}`}
                            >
                                <span className="agenda-week-day-name">{day.locale('pt-br').format('dd').charAt(0).toUpperCase()}</span>
                                <span className="agenda-week-day-number">{day.format('D')}</span>
                                <span className={`agenda-week-day-dot ${isAvailable ? 'is-visible' : ''}`} aria-hidden="true" />
                            </button>
                        );
                    })}
                </div>
            )}
        </section>
    ) : (
        <section className="agenda-filter-block" aria-label="Data">
            <div className="agenda-date-head">
                <div className="min-w-0 flex-1">
                    <h2 className="agenda-date-title">{formatDateLabel(date)}</h2>
                    {relativeDayLabel && <p className="agenda-date-subtitle">{relativeDayLabel}</p>}
                </div>
                <div className="flex items-center gap-1">
                    {!isToday && (
                        <button type="button" className="agenda-today-button" onClick={() => selectDate(calendarDisplayDate(new Date(), estabSession?.timezone))}>
                            Hoje
                        </button>
                    )}
                    <button type="button" className="agenda-day-nav-button" onClick={() => changeDay(-1)} aria-label="Dia anterior">
                        <ChevronLeft aria-hidden="true" />
                    </button>
                    <button type="button" className="agenda-day-nav-button" onClick={() => changeDay(1)} aria-label="Próximo dia">
                        <ChevronRight aria-hidden="true" />
                    </button>
                </div>
            </div>
            {datePicker}
            {legend}
        </section>
    );

    const settingsBlock = (
        <section className="agenda-filter-block" aria-label="Configurações da agenda">
            <h2 className="agenda-filter-title">Configurações</h2>
            <ul className="menu gap-0.5 p-0">
                {visibleSettingsSections.map((section) => {
                    const isActive = section.key === activeSettingsSection;
                    return (
                        <li key={section.key}>
                            <button
                                type="button"
                                className={`sf-menu-item flex min-h-10 w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-medium ${isActive ? 'is-active' : ''}`}
                                onClick={() => setParams({ action: 'settings', id: section.key })}
                                aria-current={isActive ? 'page' : undefined}
                                title={section.description}
                            >
                                <section.icon className="sf-menu-item-icon" aria-hidden="true" />
                                <span className="min-w-0 flex-1 truncate">{section.title}</span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </section>
    );

    return (
        <div className={`agenda-filter-panel ${isCompact ? 'is-compact' : 'is-sidebar'}`}>
            {attendantsBlock}
            {isSettingsView ? settingsBlock : dateBlock}
        </div>
    );
};

export default FilterAgenda;
